import React, { useState, useEffect, createRef, useContext } from "react";

import { Collapse } from "react-collapse";
import {
    AutoSizer,
    List,
    CellMeasurer,
    CellMeasurerCache,
} from "react-virtualized";
import { isEmpty } from "lodash";

import UserContext from "../../lib/UserContext";

import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import SwitchButton from "../../components/Buttons/SwitchButton";
import { ErrorAlert } from "../../utils/Alerts";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";

import MeasurementsAPI from "../../API/measurements.api";
import LookupsAPI from "../../API/lookups.api";
import ScansAPI from "../../API/scans.api";
import NoDataView from "../Shared/NoDataView";
import InformationTooltip from "../../components/Tooltips/InformationTooltip";
import { Trans, useTranslation } from "react-i18next";
import { getMappedFilters } from "../../utils/FilterHelpers";
import StepsNavButtons from "../Buttons/StepsNavButtons";

const cache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 100,
});

const SelectData = ({
    incrementStep,
    applyCloudData,
    selectedRows,
    setSelectedRows,
    scansFilterOptions,
    measurmentsFilterOptions,
    isCollect,
    setIsCollect,
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    scanAppliedFilters,
    measurementAppliedFilters,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState({});

    const [mainCollapse, setMainCollapse] = useState({});
    const [innerCollapse, setInnerCollapse] = useState({});

    const [data, setData] = useState([]);
    const [scans, setScans] = useState([]);
    const [measurements, setMeasurements] = useState([]);

    const [scansCollapse, setScansCollapse] = useState({});
    const [scansInnerCollapse, setScansInnerCollapse] = useState({});
    const [lastFetchedRow, setLastFetchedRow] = useState(0);

    const [measurementsCollapse, setMeasurementsCollapse] = useState({});
    const [measurementsInnerCollapse, setMeasurementsInnerCollapse] = useState(
        {}
    );

    const list = createRef(null);
    const context = useContext(UserContext);
    const { t } = useTranslation();

    useEffect(() => {
        setSelectedRows([]);
        fetchMaterialsMeasurements(measurementAppliedFilters).then((result) => {
            isCollect && setData(result);
            setMeasurements(result);
            setIsLoading(false);
        });
        fetchMaterialsScans(scanAppliedFilters).then((result) => {
            !isCollect && setData(result);
            setScans(result);
        });
        setCurrentUser(context.getUser());
    }, []);

    useEffect(() => {
        setMainCollapse({});
        setInnerCollapse({});
    }, [appliedFilters]);

    useEffect(() => {
        if (isCollect) {
            setData(measurements);
            setScansCollapse({ ...mainCollapse });
            setScansInnerCollapse({ ...innerCollapse });

            setMainCollapse({ ...measurementsCollapse });
            setInnerCollapse({ ...measurementsInnerCollapse });
        } else {
            setData(scans);
            setMeasurementsCollapse({ ...mainCollapse });
            setMeasurementsInnerCollapse({ ...innerCollapse });

            setMainCollapse({ ...scansCollapse });
            setInnerCollapse({ ...scansInnerCollapse });
        }
    }, [isCollect]);

    useEffect(() => {
        cache.clearAll();
        list.current?.recomputeRowHeights();
    }, [data]);

    const fetchMaterialsMeasurements = async (filters = null) => {
        const newFilters = getMappedFilters(filters);
        const result = await LookupsAPI.getMaterialsMeasurements(newFilters);
        return result.map((material) => {
            return { ...material, value: null };
        });
    };

    const fetchMaterialsScans = async (filters = null) => {
        const newFilters = getMappedFilters(filters);
        const result = await LookupsAPI.getMaterialsScans(newFilters);
        return result.map((material) => {
            return { ...material, value: null };
        });
    };

    const fetchMeasurementData = async (filterData = null) => {
        const newFilters = getMappedFilters({
            ...filterData,
            ...measurementAppliedFilters,
        });
        const result = await MeasurementsAPI.getMeasurements({
            ...newFilters,
        });
        const mappedResult = measurements.map((measurement) => {
            if (measurement?.id === result[0]?.id)
                return { ...measurement, value: result[0].value };
            return measurement;
        });
        setMeasurements(mappedResult);
        if (isCollect) setData(mappedResult || []);
    };

    const fetchScanData = async (filterData = null) => {
        const newFilters = getMappedFilters({
            ...filterData,
            ...scanAppliedFilters,
        });
        const result = await ScansAPI.getScansList({
            ...newFilters,
        });
        const mappedResult = scans.map((scan) => {
            if (scan?.id === result[0]?.id)
                return { ...scan, value: result[0].value };
            return scan;
        });
        setScans(mappedResult);
        if (!isCollect) setData(mappedResult || []);
    };

    const applyFilters = (filterData) => {
        setIsLoading(true);

        if (isCollect) {
            fetchMaterialsMeasurements(filterData)
                .then((result) => {
                    setData(result);
                    setMeasurements(result);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            fetchMaterialsScans(filterData)
                .then((result) => {
                    setData(result);
                    setScans(result);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const handleChecked = (key, index, innerIndex) => {
        const collectOrScan = isCollect ? "collect" : "scan";
        return selectedRows[`${collectOrScan}-${key}-${index}-${innerIndex}`]
            ? true
            : false;
    };

    const handleSampleChecked = (
        key,
        index,
        innerIndex,
        numberOfPoints,
        userId,
        materialName
    ) => {
        const collectOrScan = isCollect ? "collect" : "scan";
        let newSelected = { ...selectedRows };
        const selectedValues = Object.values(newSelected);
        if (
            selectedValues.length > 0 &&
            selectedValues[0].numberOfPoints !== numberOfPoints
        ) {
            ErrorAlert(t("selectDifferentPointsMessage"));
            return;
        }

        const isSelected =
            newSelected[`${collectOrScan}-${key}-${index}-${innerIndex}`];
        if (!isSelected) {
            newSelected[`${collectOrScan}-${key}-${index}-${innerIndex}`] = {
                materialId: key,
                index,
                numberOfPoints,
                userId,
                innerIndex,
                isCollect,
                materialName,
            };
        } else {
            delete newSelected[
                `${collectOrScan}-${key}-${index}-${innerIndex}`
            ];
        }

        setSelectedRows(newSelected);
    };

    const submitData = () => {
        let scansCriteria = [];
        let measurementCriteria = [];
        Object.values(selectedRows).forEach((row) => {
            const criteria = {
                materialId: row.materialId,
                userId: row.userId,
                numberOfPoints: row.numberOfPoints,
            };
            if (row.isCollect) {
                measurementCriteria.push(criteria);
            } else {
                scansCriteria.push(criteria);
            }
        });

        applyCloudData(measurementCriteria, scansCriteria);
    };

    const handleExpand = (index, innerData) => {
        let opened = { ...mainCollapse };
        opened[index] = !mainCollapse[index];
        if (opened[index] && !innerData.value) {
            setIsLoading(true);
            setLastFetchedRow(index);
            if (isCollect) {
                fetchMeasurementData({
                    materialId: innerData.id,
                }).then(() => {
                    setIsLoading(false);
                });
            } else {
                fetchScanData({
                    materialId: innerData.id,
                }).then(() => {
                    setIsLoading(false);
                });
            }
        }
        setMainCollapse(opened);

        cache.clear(index);

        // set innercells
        let collapse = { ...innerCollapse };
        if (!collapse[index]) {
            collapse[index] = {};
            setInnerCollapse(collapse);
        }
    };

    const handleInnerExpand = (index, innerIndex) => {
        let collapse = { ...innerCollapse };
        const isCollapse = collapse[index];
        isCollapse[innerIndex] = isCollapse ? !isCollapse[innerIndex] : true;

        setInnerCollapse(collapse);
        cache.clear(index);
    };

    const _getData = (index) => {
        return data[index];
    };

    const getCollapseValue = (index, innerIndex) => {
        const isCollapse = innerCollapse[index];
        return isCollapse ? isCollapse[innerIndex] : false;
    };

    const _noRowsRenderer = () => {
        return (
            <NoDataView entity={t("measurements")} includeText={!isLoading} />
        );
    };

    const _rowRenderer = ({ index, key, style, parent }) => {
        const retrievedData = _getData(index);
        return (
            <CellMeasurer
                key={key}
                cache={cache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                {({ registerChild }) => (
                    <div
                        ref={registerChild}
                        className="dataset-items border-bottom"
                        style={style}
                        key={`row-${key}`}
                    >
                        <div
                            onClick={() => handleExpand(index, retrievedData)}
                            className="dataset-items-header dataset-btn py-2 px-5 cursor-pointer"
                        >
                            <i
                                className={`fas fa-caret-${
                                    mainCollapse[index] ? "down" : "right"
                                } mr-2`}
                            />
                            {retrievedData.name}
                        </div>
                        {innerRowRenderer(retrievedData, index)}
                    </div>
                )}
            </CellMeasurer>
        );
    };

    const innerRowRenderer = (innerData, index) => {
        if (innerData.value) {
            return (
                <Collapse isOpened={mainCollapse[index]}>
                    <hr />
                    {innerData.value.map((item, itemIndex) => {
                        const isCollapsed = getCollapseValue(index, itemIndex);
                        const isFirstItem = itemIndex === 0;
                        return (
                            <div
                                key={`list-${itemIndex}`}
                                className={`dataset-items-body pb-4 ${
                                    isFirstItem && "pt-4"
                                }`}
                            >
                                <div
                                    className="dataset-content px-10 py-1 cursor-pointer"
                                    onClick={() =>
                                        handleInnerExpand(index, itemIndex)
                                    }
                                >
                                    <i
                                        className={`mr-2 fas fa-caret-${
                                            isCollapsed ? "down" : "right"
                                        }`}
                                    />
                                    {item.email}
                                    {item.email === currentUser.email
                                        ? t("me")
                                        : ""}
                                </div>
                                {pointsRowRenderer(
                                    isCollapsed,
                                    innerData.id,
                                    index,
                                    item,
                                    itemIndex
                                )}
                            </div>
                        );
                    })}
                </Collapse>
            );
        }
    };

    const pointsRowRenderer = (isCollapsed, id, index, item, itemIndex) => {
        return (
            <Collapse isOpened={isCollapsed}>
                <div className="dataset-sub-content px-14">
                    <div className="dataset-items-sub-level-body mb-3">
                        <div className="grid grid-cols-3 gap-4 mt-1 px-4 uppercase dataset-items-header dataset-btn">
                            <label>
                                <Trans i18nKey="numberOfPoints">
                                    Number of Points
                                </Trans>
                            </label>
                            <label className="text-center">
                                <Trans i18nKey="numberOfMeasurements">
                                    Number of Measurements
                                </Trans>
                            </label>
                            <label className="text-center">
                                <Trans i18nKey="containsImportedData">
                                    Contains Imported Data
                                </Trans>
                            </label>
                        </div>
                        <hr />
                        {item.points.map((point, subIndex) => {
                            return (
                                <div
                                    key={`sublist-${subIndex}`}
                                    className="grid grid-cols-3 gap-4 mt-4 ml-4"
                                >
                                    <div className="checkbox-wrapper-input filter-checker-btn">
                                        <input
                                            type="checkbox"
                                            className="checkbox-element"
                                            id={`checkbox-ele-${index}-${itemIndex}-${subIndex}-point`}
                                            checked={handleChecked(
                                                id,
                                                itemIndex,
                                                subIndex
                                            )}
                                            onChange={() =>
                                                handleSampleChecked(
                                                    id,
                                                    itemIndex,
                                                    subIndex,
                                                    point.numberOfPoints,
                                                    item.id,
                                                    data[index].name
                                                )
                                            }
                                        />
                                        <label
                                            className="black"
                                            htmlFor={`checkbox-ele-${index}-${itemIndex}-${subIndex}-point`}
                                        >
                                            <b className="mr-1">
                                                {point.numberOfPoints}
                                            </b>
                                            <Trans i18nKey="pointsDataset">
                                                Points Dataset
                                            </Trans>
                                        </label>
                                    </div>
                                    <label className="text-center">
                                        {point.count}
                                    </label>

                                    <label className="text-center text-xs">
                                        {point.containsImported ? (
                                            <i className="fas fa-check text-primary-blue fa-xs" />
                                        ) : (
                                            "--"
                                        )}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Collapse>
        );
    };

    return (
        <>
            <div id="customer-content" className="customer-content">
                <header className="main-content-header flex items-center justify-between">
                    <div className="flex justify-start">
                        <h1 className="h1 pr-1">
                            <Trans i18nKey="measurements">Measurements</Trans>
                        </h1>
                        <div className="self-center">
                            <InformationTooltip
                                message={t("dataTimeTooltip", {
                                    entity: "Measurements",
                                })}
                            />
                        </div>
                    </div>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={
                                isCollect
                                    ? measurmentsFilterOptions
                                    : scansFilterOptions
                            }
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component={
                                isCollect
                                    ? "measurement-collect"
                                    : "measurement-scan"
                            }
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                            disabled={isLoading}
                        />
                    </div>
                </header>
            </div>

            <div className="card-table mb-5">
                <div className="mb-3">
                    <SwitchButton
                        firstButton={t("collect")}
                        secondButton={t("scan")}
                        isActive={isCollect}
                        setIsActive={setIsCollect}
                        disabled={isLoading}
                    />
                </div>
                <LoadingOverlay active={isLoading} text="Loading...">
                    <div className="dataset-wrapper ag-grid-height">
                        <AutoSizer>
                            {({ width, height }) => (
                                <List
                                    className="List"
                                    height={height}
                                    noRowsRenderer={_noRowsRenderer}
                                    rowCount={data?.length || 0}
                                    rowHeight={cache.rowHeight}
                                    rowRenderer={_rowRenderer}
                                    deferredMeasurementCache={cache}
                                    width={width}
                                    ref={list}
                                    scrollToIndex={lastFetchedRow}
                                    scrollToAlignment="center"
                                />
                            )}
                        </AutoSizer>
                    </div>
                </LoadingOverlay>
            </div>
            <StepsNavButtons
                next={{
                    action: () => {
                        submitData();
                        incrementStep();
                    },
                    label: t("next"),
                    disabled: isEmpty(selectedRows),
                }}
            />
        </>
    );
};

export default SelectData;
